import {Component, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {CreateAdminUserRequest, UpdateAdminUserRequest, AdminUser} from "@app/_models/api";
import {NgbModal, NgbModalRef, NgbOffcanvas} from "@ng-bootstrap/ng-bootstrap";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AsyncPipe} from "@angular/common";
import {Observable} from "rxjs";
import {SortableDirective} from "@app/_directives/sortable.directive";
import {AdminUsersService} from "@app/_services/admin/admin-users.service";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {ToastService} from "@app/_services/toast.service";
import {ApiService} from "@app/_services/api.service";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";
import {openUserActivityDrawer} from "@app/_components/user-activity-drawer/user-activity-drawer.component";

@Component({
    selector: 'app-admin-users',
    imports: [
        TablePagerComponent,
        ReactiveFormsModule,
        FormsModule,
        AsyncPipe,
        ModalConfirmComponent
    ],
    templateUrl: './admin-users.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./admin-users.component.scss', '../app.component.scss']
})
export class AdminUsersComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('addUserModal') private addUserModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild('editModal') private editModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;
    private addUserModalRef!: NgbModalRef
    private onEditModalRef!: NgbModalRef

    savingData: boolean = false;
    total$: Observable<number>;
    users$: Observable<AdminUser[]>;

    addUserForm = new FormGroup({
        fullName: new FormControl("", Validators.required),
        email: new FormControl("", [Validators.required, Validators.email]),
    });

    editForm = new FormGroup({
        id: new FormControl("", Validators.required),
        fullName: new FormControl("", Validators.required),
        email: new FormControl("", [Validators.required, Validators.email]),
        isEnabled: new FormControl(false, Validators.required),
    });

    constructor(
        private route: ActivatedRoute,
        private toastService: ToastService,
        private modalService: NgbModal,
        private apiService: ApiService,
        private offcanvasService: NgbOffcanvas,
        public service: AdminUsersService
    ) {
        this.service.data = this.route.snapshot.data['resolvedData'].data;
        this.users$ = this.service.data$;
        this.total$ = this.service.total$;
        this.service.refreshDataSource();
    }

    onAddUser() {
        this.addUserForm.reset();
        this.addUserModalRef = this.modalService.open(this.addUserModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static",
        });
    }

    onAddUserDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.addUserForm, () => this.addUserModalRef.dismiss());
    }

    onAddUserSubmit() {
        if (this.addUserForm.invalid) {
            if (this.addUserForm.controls.email.getError('email')) {
                this.toastService.showDanger("Invalid Email Address", "A valid email address is required.");
                return;
            }

            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        this.savingData = true;
        const user = this.addUserForm.value;

        let body: CreateAdminUserRequest = {
            fullName: user.fullName!,
            email: user.email!,
        };

        this.apiService.createAdminUser(body).subscribe({
            next: (response) => {
                this.toastService.showSuccess("Success", "User Successfully Added");
                this.service.data = this.service.data.concat(response);
                this.service.refreshDataSource();
            },
            error: () => {
                this.savingData = false;
                this.toastService.showDanger("Request Failed", "Please try again.");
            },
            complete: () => {
                this.savingData = false;
                this.addUserModalRef.close();
            }
        });
    }

    onEdit(element: AdminUser) {
        this.editForm.reset();
        this.editForm.setValue({
            id: element.id,
            fullName: element.fullName,
            email: element.email,
            isEnabled: element.isEnabled,
        });

        this.onEditModalRef = this.modalService.open(this.editModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static",
        });
    }

    onEditDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.editForm, () => this.onEditModalRef.dismiss());
    }

    onSubmit() {
        if (this.editForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        this.savingData = true;
        const user = this.editForm.value;

        let body: UpdateAdminUserRequest = {
            fullName: user.fullName!,
            isEnabled: user.isEnabled!,
        };

        this.apiService.updateAdminUser(user.id!, body).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "User Successfully Updated");
                this.service.data = this.service.data.map(data => {
                    if (data.id != user.id) {
                        return data;
                    }

                    const temp = Object.assign({}, data);
                    Object.assign(temp, body);
                    return temp;
                });

                this.service.refreshDataSource();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Please try again.");
                throw e;
            },
            complete: () => {
                this.savingData = false;
                this.onEditModalRef.close();
            }
        });
    }

    confirmDeleteUser(user: AdminUser) {
        this.modalConfirmComponent.open("Are you sure you want to delete user?", user.email);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.apiService.deleteAdminUser(user.id).subscribe({
                    next: () => {
                        this.toastService.showSuccess("Success", "User Successfully Removed.");
                        this.service.data = this.service.data.filter(data => data.id != user.id);
                        this.service.refreshDataSource();
                    },
                    error: (e) => {
                        this.toastService.showDanger("Request failed", "Please try again.");
                        throw e;
                    },
                    complete: () => {
                    }
                });
            },
            () => {
            },
        );
    }

    onActivity(user: AdminUser) {
        openUserActivityDrawer(this.offcanvasService, user);
    }
}
